import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import TopicCard from '@/components/feature/TopicCard';
import FaqAccordion from '@/components/feature/FaqAccordion';
import SectionHeading from '@/components/base/SectionHeading';
import Hero from '@/pages/home/components/Hero';
import TrendingRanking from '@/pages/home/components/TrendingRanking';
import CategoryGrid from '@/pages/home/components/CategoryGrid';
import StatsStrip from '@/pages/home/components/StatsStrip';
import useSeo from '@/hooks/useSeo';
import { faqs, topics } from '@/mocks/topics';

export default function Home() {
  const featured = topics.find((t) => t.featured) ?? topics[0];
  const byHeat = [...topics].sort((a, b) => b.heat - a.heat);
  const secondary = byHeat.filter((t) => t.slug !== featured.slug).slice(0, 3);
  const ranking = byHeat.slice(0, 5);

  const usedSlugs = new Set([featured.slug, ...secondary.map((t) => t.slug)]);
  const latest = topics.filter((t) => !usedSlugs.has(t.slug)).slice(0, 6);

  useSeo({
    title: '锐察社 - 社会爆料议题盘点｜行业内幕·乱象追踪·维权参考',
    description:
      '锐察社聚焦社会爆料，盘点行业内幕、消费乱象、维权纠纷与争议事件。汇总热门议题、梳理事件脉络并提供维权参考，全程追踪最新动态。',
    keywords: '社会爆料,社会爆料行业内幕,社会爆料乱象,社会爆料维权,社会爆料纠纷,议题盘点',
    path: '/',
    type: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: '锐察社',
        url: 'https://sjbxw.cn/',
        description: '聚焦社会爆料，盘点行业内幕、消费乱象、维权纠纷与争议事件。',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://sjbxw.cn/topics?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: '锐察社',
        url: 'https://sjbxw.cn/',
        email: 'contact@ruicha-observer.com',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  });

  return (
    <SiteLayout>
      <Hero featured={featured} secondary={secondary} />

      <div className="mx-auto max-w-[1200px] px-4 pt-10 md:px-6 md:pt-14">
        <StatsStrip />
      </div>

      <section className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <TrendingRanking topics={ranking} />
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-6">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1 font-label text-xs text-background-50">
                <i className="ri-book-marked-line" aria-hidden="true"></i>
                实用专题
              </span>
              <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">
                遇到类似问题，先看维权指南
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                从固定证据到协商沟通，一份可以直接照着做的处理清单，帮你把被动变成主动。
              </p>
              <Link
                to="/guide"
                className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-foreground-900 px-4 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-foreground-950"
              >
                打开维权指南
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="mt-6 rounded-lg border border-background-300 bg-background-100 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">我们的内容原则</h3>
              <ul className="mt-4 space-y-3 text-sm leading-relaxed text-foreground-700">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
                  以议题和现象为单位，不作未核实的指控
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
                  同时呈现多方说法，标注尚未定论的部分
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
                  每个议题都尽量给出可操作的处理建议
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-4 pb-10 md:px-6 md:pb-14">
        <CategoryGrid />
      </section>

      <section className="border-y border-background-300 bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
          <SectionHeading
            eyebrow="Latest"
            title="最新爆料盘点"
            description="持续更新的议题梳理，覆盖消费、住房、教育与服务等多个生活场景。"
            linkText="查看全部议题"
            linkTo="/topics"
          />
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {latest.map((topic) => (
              <TopicCard key={topic.slug} topic={topic} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
        <div className="relative overflow-hidden rounded-lg">
          <div className="absolute inset-0 h-full w-full">
            <img
              src="https://readdy.ai/api/search-image?query=Hands%20writing%20notes%20beside%20a%20stack%20of%20documents%20and%20a%20pen%20on%20a%20warm%20wooden%20desk%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20clean%20composition%2C%20no%20text&width=1400&height=700&seq=rc-guide-01&orientation=landscape"
              alt="整理维权材料与证据的场景配图"
              title="维权指南 - 社会爆料"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40"></div>
          <div className="relative px-6 py-12 md:px-12 md:py-16">
            <div className="max-w-xl">
              <p className="font-label text-xs uppercase tracking-[0.2em] text-accent-400">
                维权指南
              </p>
              <h2 className="mt-3 font-heading text-2xl font-black text-background-50 md:text-4xl">
                把情绪变成证据，把证据变成方法
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-background-200 md:text-base">
                多数纠纷的差距不在于道理，而在于证据是否完整、沟通是否留痕。这份指南整理了从发生到处理的完整流程。
              </p>
              <Link
                to="/guide"
                className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm text-foreground-950 transition-colors hover:bg-accent-400"
              >
                查看完整流程
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-4 pb-14 md:px-6 md:pb-20">
        <SectionHeading
          eyebrow="FAQ"
          title="读者常见问题"
          description="关于内容来源、使用方式与线索反馈，你可能会关心的几个问题。"
        />
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <FaqAccordion items={faqs} />
          </div>
          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-300 bg-background-100 p-6">
              <h3 className="font-heading text-lg font-bold text-foreground-950">还有疑问？</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                如果你遇到的情况不在上述范围内，或者想补充某个议题的线索，欢迎直接联系我们。
              </p>
              <Link
                to="/about#contact"
                className="mt-4 inline-flex items-center gap-1.5 font-label text-sm text-primary-700 transition-colors hover:text-primary-800"
              >
                联系锐察社
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
}